import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { empresasApi } from '../../api/empresas'
import { usuariosApi } from '../../api/usuarios'
import { User } from '../../types/auth'

interface VincularEmpresasDialogProps {
  open: boolean
  onClose: () => void
  usuario: User | null
}

const VincularEmpresasDialog: React.FC<VincularEmpresasDialogProps> = ({
  open,
  onClose,
  usuario,
}) => {
  const queryClient = useQueryClient()
  const [empresasVinculadas, setEmpresasVinculadas] = useState<number[]>([])

  // Buscar todas as empresas
  const { data: todasEmpresas = [], isLoading: loadingEmpresas } = useQuery({
    queryKey: ['empresas'],
    queryFn: () => empresasApi.list(),
    enabled: open,
  })
  const safeTodasEmpresas = Array.isArray(todasEmpresas) ? todasEmpresas : []

  // Buscar empresas vinculadas ao usuário (via API quando disponível)
  // Por enquanto, vamos usar uma lista vazia e implementar depois
  useEffect(() => {
    if (open && usuario) {
      // TODO: Buscar empresas vinculadas do backend
      // Por enquanto, vamos assumir que não há endpoint para isso
      setEmpresasVinculadas([])
    }
  }, [open, usuario])

  // Mutação para vincular empresa
  const vincularMutation = useMutation({
    mutationFn: ({ usuarioId, empresaId }: { usuarioId: number; empresaId: number }) =>
      usuariosApi.vincularEmpresa(usuarioId, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      // Atualizar lista local
      // TODO: Implementar quando tiver endpoint para buscar empresas vinculadas
    },
  })

  // Mutação para desvincular empresa
  const desvincularMutation = useMutation({
    mutationFn: ({ usuarioId, empresaId }: { usuarioId: number; empresaId: number }) =>
      usuariosApi.desvincularEmpresa(usuarioId, empresaId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      // Atualizar lista local
      setEmpresasVinculadas((prev) => prev.filter((id) => id !== variables.empresaId))
    },
  })

  const handleVincular = async (empresaId: number) => {
    if (usuario) {
      try {
        await vincularMutation.mutateAsync({ usuarioId: usuario.id, empresaId })
        setEmpresasVinculadas((prev) => [...prev, empresaId])
      } catch (error) {
        // Erro já é tratado pelo React Query, apenas logar
        // logger.error('Erro ao vincular empresa:', error)  // Comentado - erro já tratado
      }
    }
  }

  const handleDesvincular = async (empresaId: number) => {
    if (usuario) {
      try {
        await desvincularMutation.mutateAsync({ usuarioId: usuario.id, empresaId })
      } catch (error) {
        // Erro já é tratado pelo React Query, apenas logar
        // logger.error('Erro ao desvincular empresa:', error)  // Comentado - erro já tratado
      }
    }
  }

  const empresasVinculadasList = safeTodasEmpresas.filter((e) => empresasVinculadas.includes(e.id))
  const empresasNaoVinculadas = safeTodasEmpresas.filter((e) => !empresasVinculadas.includes(e.id))

  if (!usuario) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Gerenciar Empresas - {usuario.name}
      </DialogTitle>
      <DialogContent>
        {loadingEmpresas ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Empresas Vinculadas
            </Typography>
            {empresasVinculadasList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nenhuma empresa vinculada
              </Typography>
            ) : (
              <List>
                {empresasVinculadasList.map((empresa) => (
                  <ListItem
                    key={empresa.id}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDesvincular(empresa.id)}
                        disabled={desvincularMutation.isPending}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={empresa.nome}
                      secondary={empresa.cnpj || `ID: ${empresa.id}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Empresas Disponíveis
            </Typography>
            {empresasNaoVinculadas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Todas as empresas já estão vinculadas
              </Typography>
            ) : (
              <List>
                {empresasNaoVinculadas.map((empresa) => (
                  <ListItem
                    key={empresa.id}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleVincular(empresa.id)}
                        disabled={vincularMutation.isPending}
                      >
                        Vincular
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={empresa.nome}
                      secondary={empresa.cnpj || `ID: ${empresa.id}`}
                    />
                    {empresa.status && (
                      <Chip
                        label={empresa.status === 'A' ? 'Ativo' : 'Inativo'}
                        color={empresa.status === 'A' ? 'success' : 'default'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

export default VincularEmpresasDialog

